<script lang="ts" setup>
import type { FormSubmitEvent, Form } from "#ui/types";
import { type InferType, object, string, boolean } from "yup";
import { faSpinnerThird } from "@fortawesome/pro-light-svg-icons";

const schema = object().shape({
    email: string().email().required(),
    password: string().required(),
    remember_me: boolean(),
});

type Schema = InferType<typeof schema>;

const { login } = useSanctumAuth();

const form = ref<Form<Schema>>();
const unauthorized = ref<boolean>(false);
const state = reactive<Schema>({
    email: "",
    password: "",
    remember_me: true,
});

const { onSubmit, submitting } = useFormSubmission<Schema, void>(
    form,
    async (event) => {
        unauthorized.value = false;
        return login(event.data);
    },
    {
        onError: (error) => {
            if (error.code === 401) {
                unauthorized.value = true;
            }
        },
        onDone: () => (submitting.value = false),
    },
);

function goToPasswordReset() {
    let link = "/password/reset";
    if (state.email) {
        link += `?email=${state.email}`;
    }
    useRouter().push(link);
}
</script>

<template>
    <UForm
        ref="form"
        :schema="schema"
        :state="state"
        :validate-on="['submit']"
        @submit="onSubmit"
    >
        <AppLogo class="w-32 mb-10 mx-auto" />
        <UCard :ui="{ background: 'bg-white' }">
            <template #header>
                <div class="items-center text-center justify-between">
                    <h1 class="text-xl font-medium font-display">Sign in</h1>
                </div>
            </template>
            <div class="flex flex-col gap-3 w-96">
                <UFormGroup
                    name="email"
                    help="We will never share your email with anyone else."
                    label="Email"
                >
                    <UInput
                        v-model="state.email"
                        placeholder="jeroen@hiredata.com"
                        size="md"
                        type="email"
                        autocomplete="email"
                    />
                </UFormGroup>
                <UFormGroup label="Password" name="password">
                    <UInput
                        v-model="state.password"
                        placeholder="••••••••••"
                        size="md"
                        type="password"
                        autocomplete="current-password"
                    />
                    <p
                        v-if="unauthorized"
                        class="mt-2 text-red-500 dark:text-red-400 text-sm"
                    >
                        The password is incorrect. Please try again or
                        <ULink
                            class="text-blue-600 hover:underline"
                            @click="goToPasswordReset"
                            >reset your password.
                        </ULink>
                    </p>
                </UFormGroup>
            </div>
            <template #footer>
                <div class="grid grid-cols-2 gap-3">
                    <UButton
                        block
                        color="primary"
                        :disabled="submitting"
                        type="submit"
                    >
                        <template #leading v-if="submitting">
                            <FontAwesomeIcon :icon="faSpinnerThird" spin />
                        </template>
                        Login
                    </UButton>
                    <UButton block color="gray" to="/register">
                        Register
                    </UButton>
                </div>
                <div class="text-center mt-3">
                    <UButton
                        class="detail-button hover:underline -ml-2.5"
                        color="white"
                        variant="ghost"
                        @click="goToPasswordReset"
                    >
                        Forgot your password?
                    </UButton>
                </div>
            </template>
        </UCard>
    </UForm>
</template>
